import { callSingle, callAction } from '@/lib/rpc/facade';
import type { SingleResult, ActionResult } from '@/lib/rpc/types';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type SessionInfo = {
  user: {
    id: string;
    email?: string;
    roles?: string[];
    is_authenticated: boolean;
  } | null;
  session?: {
    access_token?: string;
    refresh_token?: string;
    expires_at?: number;
  };
};

export type CurrentSessionInfo = {
  ok: boolean;
  data?: {
    user: (Pick<User, 'id' | 'email'> & { roles?: string[]; is_authenticated: boolean }) | null;
    session?: { access_token: string | null; refresh_token: string | null; expires_at: number | null };
  };
  error?: any;
  source: 'supabase-auth';
};

/** Низкоуровневый хелпер: вернуть сырую сессию (для фасада/хуков) */
export async function getCurrentSession(): Promise<Session | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data?.session ?? null;
}

/** Высокоуровневый хелпер: унифицированный объект для UI */
export async function getCurrentSessionInfo(): Promise<CurrentSessionInfo> {
  try {
    // E2E Testing Bypass
    if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
      const email = window.localStorage.getItem('e2e_user_email') || 'admin@test.com';
      const userId = 'test-admin-user-id';

      return {
        ok: true,
        data: {
          user: {
            id: userId,
            email,
            roles: ['admin'],
            is_authenticated: true,
          },
          session: {
            access_token: 'mock-e2e-access-token',
            refresh_token: 'mock-e2e-refresh-token',
            expires_at: Math.floor(Date.now() / 1000) + 3600,
          },
        },
        source: 'supabase-auth',
      };
    }

    // Try to refresh session first if it might be stale
    await supabase.auth.refreshSession();

    const session = await getCurrentSession();
    if (!session?.user) {
      return {
        ok: true,
        data: { user: null, session: undefined },
        source: 'supabase-auth',
      };
    }
    return {
      ok: true,
      data: {
        user: {
          id: session.user.id,
          email: session.user.email ?? undefined,
          roles: [],
          is_authenticated: true,
        },
        session: {
          access_token: (session as any).access_token ?? null,
          refresh_token: (session as any).refresh_token ?? null,
          expires_at: (session as any).expires_at ?? null,
        },
      },
      source: 'supabase-auth',
    };
  } catch (error) {
    return { ok: false, error, source: 'supabase-auth' };
  }
}

export async function refreshSession(): Promise<SingleResult<SessionInfo>> {
  return callSingle<SessionInfo>('auth_refresh_session', {});
}

export async function signOut(): Promise<ActionResult> {
  // CRITICAL: Must sign out from Supabase Auth locally to clear browser session
  // The RPC call alone doesn't clear the local session cookies/storage
  const { error } = await supabase.auth.signOut();
  
  if (error) {
    console.error('[Auth] Sign out failed:', error);
    return { ok: false, error: error.message, source: 'action', disabled: false };
  }
  
  // Also notify backend (optional, session is already invalidated client-side)
  try {
    await callAction('auth_sign_out', {});
  } catch {
    // Ignore RPC errors - local signout succeeded
  }
  
  return { ok: true, data: null, source: 'action', disabled: false };
}